"use client";

import { useEffect } from "react";

// Stores ad click IDs and UTM values in a first-party cookie so the form API
// routes can attach them to every lead, whichever page the form is on.
const COOKIE_NAME = "tlm_attr";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days, the Google Ads click window
const KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

type Attribution = Partial<Record<(typeof KEYS)[number], string>> & {
  landing_page?: string;
  referrer?: string;
  ts?: string;
};

function readCookie(): Attribution | null {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.split("=").slice(1).join("=")));
  } catch {
    return null;
  }
}

export default function AttributionCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl: Attribution = {};
      for (const key of KEYS) {
        const value = params.get(key);
        if (value) fromUrl[key] = value.slice(0, 300);
      }

      const existing = readCookie();
      const hasNewTouch = Object.keys(fromUrl).length > 0;

      // Keep the stored touch unless this visit arrived with new ad parameters.
      if (existing && !hasNewTouch) return;

      const next: Attribution = {
        ...fromUrl,
        landing_page: (window.location.origin + window.location.pathname).slice(
          0,
          300
        ),
        referrer: document.referrer ? document.referrer.slice(0, 300) : "",
        ts: new Date().toISOString(),
      };

      const host = window.location.hostname;
      const domain = host.endsWith("trichos.in") ? "; Domain=.trichos.in" : "";
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
        JSON.stringify(next)
      )}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${domain}${secure}`;
    } catch {
      // Attribution is best-effort and must never break the page.
    }
  }, []);

  return null;
}
